import { useState, useEffect, useRef, useContext } from 'react'
import { Backdrop, CircularProgress, Popover, Box, makeStyles } from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import SearchIcon from '@material-ui/icons/Search'
import Axios from 'axios'

import delay from '../utility/delay'
import {
  orderStatusToStepMap,
  interOrderStatusToStepMap,
  groupByOrderStatus,
} from '../utility/trackingUtility'

import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { event } from '../../libs/ga'
import { POSITION } from '../../libs/ga/gaAction'

import { Context } from '../../context'
import NewShell from './NewShell'
import T from '../home-new/tokens'
import TrackingHeroNew from './tracking-new/TrackingHeroNew'
import TrackingPromoNew from './tracking-new/TrackingPromoNew'
import TrackingResultsNew from './tracking-new/TrackingResultsNew'
import HelpBanner from './service-new/HelpBanner'

// NEW-design (?new) variant of pages/tracking.js — built to the tracking
// mockup (new-design-other/tracking/single.png). The old /tracking (no
// ?new) page keeps its own inline content untouched via the guard in
// pages/tracking.js. All chrome (HeaderNew + FooterNew + Sukhumvit webfont)
// comes from NewShell.
//
// All search/Axios calls, tracking state, order-status→step mapping and
// i18n keys below are UNCHANGED from the previous version of this file —
// only the presentation (hero/search card, promo banner, results panel,
// help CTA) has been rebuilt against the new-design tokens/components. The
// "ต้องการความช่วยเหลือ..." help banner reuses ServiceNew's HelpBanner
// verbatim (identical copy + headset illustration in the mockup, so no new
// component needed); the results view reuses the SAME shared (old-design)
// Steps/DetailCard/StatusCard/CollapseCard/CardContainer/TrackingTab
// components via the new TrackingResultsNew wrapper, imported read-only.
const useStyles = makeStyles((theme) => ({
  notFoundSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '64px 16px',
    fontFamily: T.font.body,
    [theme.breakpoints.down('xs')]: {
      padding: '40px 16px',
    },
  },
  notFoundText: {
    fontWeight: 600,
    fontSize: 28,
    color: T.color.ink,
    fontFamily: T.font.heading,
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      fontSize: 18,
    },
  },
  pleaseTryAgain: {
    fontSize: 16,
    color: T.color.muted,
    fontFamily: T.font.body,
    [theme.breakpoints.down('xs')]: {
      fontSize: 14,
    },
  },
  popoverText: {
    fontFamily: T.font.body,
    fontSize: 14,
    color: T.color.ink,
  },
}))

const LIMIT_SEARCH = 20

const TrackingNew = () => {
  const Router = useRouter()
  const { mode, tracking_code, typeid } = Router.query
  const { onRouting } = useContext(Context)

  const [trackingNumber, setTrackingNumber] = useState('') // always equal search field text
  const [trackingErrorNumber, setTrackingErrorNumber] = useState('') // will be the last number that failed to search
  const [trackingMode, setTrackingMode] = useState('domestic')
  const [trackingData, setTrackingData] = useState(undefined)
  const [isNotFound, setNotFoundStatus] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [tab, setTab] = useState('all')
  const [anchorEl, setAnchorEl] = useState(null)
  const searchFieldRef = useRef(null)
  const [open, setOpen] = useState('')
  const classes = useStyles()

  const { t } = useTranslation('tracking')

  const setTrackingModeFromUrl = (mode) => {
    if (mode === 'inter') setTrackingMode('inter')
    else setTrackingMode('domestic')
  }

  const handleSearchInter = async (trackingNumber) => {
    const temp = trackingNumber.replaceAll(' ', '').split(',')
    const trackingIdList = new Set(temp)
    let promises = []
    let data = []
    let courierCode = []

    // เช็ค limit จำนวนที่จะ search ได้ต่อครั้ง
    if (trackingIdList.size > LIMIT_SEARCH) {
      setAnchorEl(searchFieldRef.current)

      return
    }

    const fetchData = async (id) => {
      try {
        const response = await Axios.post('/api/tracking/inter', {
          tracking_code: id,
        })

        const processRes = response.data

        data = [...data, processRes]
        setNotFoundStatus(false)
      } catch (err) {
        setNotFoundStatus(true)
        setTrackingErrorNumber((pre) => (pre ? `${pre}, ${id}` : id))
      }
    }

    if (trackingIdList.size > 0) {
      setTrackingData([])
      setTrackingErrorNumber('')

      for (let v of trackingIdList) {
        promises.push(fetchData(v))
      }

      setLoading(() => true)
      await Promise.all(promises)
      await delay(1500)
      if (data.length === trackingIdList.size) {
        const arr = []

        courierCode = data.map((d) => d.courier_code)

        for (let id of trackingIdList) {
          let find = data.find(
            (d) =>
              d.courier_tracking_code?.toUpperCase() === id.toUpperCase() ||
              d.tracking_code?.toUpperCase() === id.toUpperCase()
          )

          if (!arr.includes(find)) {
            arr.push(find)
          }
        }

        const result = groupByOrderStatus(arr)
        setTrackingData(result)

        if (window.gtag) {
          event({
            action: POSITION.TRACKING.SEARCH_TEXT_FIELD.action,
            event_category: POSITION.TRACKING.SEARCH_TEXT_FIELD.event_category,
            value: { amount: trackingIdList.size, courier_code: courierCode },
          })
        }
      } else {
        setNotFoundStatus(true)
      }
      setLoading(() => false)
    }
  }

  const handleSearchDomestic = async (trackingNumber) => {
    const temp = trackingNumber.replaceAll(' ', '').split(',')
    const trackingIdList = new Set(temp)
    let promises = []
    let data = []
    let courierCode = []

    // เช็ค limit จำนวนที่จะ search ได้ต่อครั้ง
    if (trackingIdList.size > LIMIT_SEARCH) {
      setAnchorEl(searchFieldRef.current)

      return
    }

    const fetchData = async (id) => {
      try {
        const response = await Axios.post('/api/tracking/domestic', {
          tracking_code: id,
        })

        const processRes = response.data

        data = [...data, processRes]
        setNotFoundStatus(false)
      } catch (err) {
        setNotFoundStatus(true)
        setTrackingErrorNumber((pre) => (pre ? `${pre}, ${id}` : id))
      }
    }

    setTrackingData([])
    setTrackingErrorNumber('')

    for (let v of trackingIdList) {
      promises.push(fetchData(v))
    }

    setLoading(() => true)
    await Promise.all(promises)
    await delay(1500)

    if (data.length === trackingIdList.size) {
      const arr = []

      courierCode = data.map((d) => d.courier_code)

      for (let id of trackingIdList) {
        let find = data.find(
          (d) =>
            d.courier_tracking_code?.toUpperCase() === id.toUpperCase() ||
            d.tracking_code?.toUpperCase() === id.toUpperCase()
        )

        if (!arr.includes(find)) {
          arr.push(find)
        }
      }

      const result = groupByOrderStatus(arr)
      setTrackingData(result)

      if (window.gtag) {
        event({
          action: POSITION.TRACKING.SEARCH_TEXT_FIELD.action,
          event_category: POSITION.TRACKING.SEARCH_TEXT_FIELD.event_category,
          value: { amount: trackingIdList.size, courier_code: courierCode },
        })
      }
    } else {
      setNotFoundStatus(true)
    }

    setLoading(() => false)
  }

  const handleSearch = (
    trackingMode = 'domestic',
    trackingNumber,
    isSubmitSearch = false
  ) => {
    if (!trackingNumber) return

    if (isSubmitSearch) {
      if ( ! tracking_code || tracking_code.toUpperCase() !== trackingNumber.toUpperCase() ) {
        // NEW is the default design now, so the self-re-route query stays
        // clean — the page wrapper renders the new variant by default.
        onRouting(
          '/tracking',
          `mode=${trackingMode}&tracking_code=${trackingNumber}`,
          {
            isReplace: true,
          }
        )
      }
    }

    if (trackingMode === 'inter') handleSearchInter(trackingNumber)
    else handleSearchDomestic(trackingNumber)

    setTab('all')
  }

  const handleCloseAnchor = () => {
    setAnchorEl(null)
  }

  const mapStatusToStep = (deliveryType, status) => {
    if (deliveryType === 'inter') {
      return interOrderStatusToStepMap[status] ?? -1
    } else if (deliveryType === 'domestic') {
      return orderStatusToStepMap[status] ?? -1
    }
    return -1
  }

  useEffect(() => {
    if (!Router.isReady) return

    if (!isLoading) {
      const searchMode = mode || typeid

      setTrackingNumber(tracking_code)
      setTrackingModeFromUrl(searchMode)

      handleSearch(searchMode, tracking_code)
    }
  }, [Router.isReady, mode, tracking_code])

  const isRednerExtraTimeLine = (d) => {
    if (d) {
      const { courier_code } = d
      return ['TP2', 'THP', 'T2P'].includes(courier_code)
    } else {
      return false
    }
  }

  const tmpTrackingState = (d) => {
    if (d) {
      const { state } = d
      return state
    } else {
      return null
    }
  }

  const trackingState = (d) => {
    const _isRednerExtraTimeLine = isRednerExtraTimeLine(d)
    const _tmpTrackingState = tmpTrackingState(d)
    const result =
      _tmpTrackingState && _isRednerExtraTimeLine
        ? [
            {
              datetime: d.datetime_shipping,
              description: t('thaipost-start-description'),
              location: t('thaipost-start-location'),
            },
            ..._tmpTrackingState,
          ]
        : _tmpTrackingState
        ? [..._tmpTrackingState]
        : null

    return result
  }

  const handleOpen = (id, offsetTop) => {
    if (id === open) {
      setOpen((pre) => `close-${pre}`)
    } else {
      setOpen(id)
      window.scrollTo(0, offsetTop)
    }
  }

  return (
    <NewShell
      title={t('new.meta.title')}
      keywords={t('new.meta.keywords')}
      description={t('new.meta.description')}
    >
      <TrackingHeroNew
        trackingMode={trackingMode}
        onModeChange={setTrackingMode}
        trackingNumber={trackingNumber}
        onNumberChange={setTrackingNumber}
        onSubmit={() => handleSearch(trackingMode, trackingNumber, true)}
        searchFieldRef={searchFieldRef}
        // Mockup-exact copy for the ?new variant only — kept in the tracking
        // namespace under `new.form.*` (separate from the shared
        // t('domestic')/t('inter')/t('tracking-no') keys, which differ
        // slightly in wording — "ส่งระหว่างประเทศ", "รหัสพัสดุ" — and are
        // still used as-is by the OLD (no ?new) page).
        domesticLabel={t('new.form.domestic')}
        interLabel={t('new.form.inter')}
        placeholder={t('new.form.placeholder')}
        submitLabel={t('new.form.submit')}
      />

      {/* โชว์ popup limit search */}
      <Popover
        open={Boolean(anchorEl)}
        onClose={handleCloseAnchor}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px',
          }}
        >
          <WarningIcon style={{ color: '#EEEE33', marginRight: '5px' }} />
          <span className={classes.popoverText}>{`${t('search-limit')} ${LIMIT_SEARCH} ${t(
            'search-unit'
          )}`}</span>
        </Box>
      </Popover>

      {/* Promo banner sits directly below the search card ONLY while no status
          result is shown. Once a status is checked (results present), it moves
          BELOW the results content instead — see after the results block. */}
      {!(trackingData && !isNotFound && !isLoading) && <TrackingPromoNew />}

      {trackingData && !isNotFound && !isLoading && (
        <TrackingResultsNew
          t={t}
          trackingData={trackingData}
          tab={tab}
          setTab={setTab}
          open={open}
          setOpen={setOpen}
          handleOpen={handleOpen}
          mapStatusToStep={mapStatusToStep}
          trackingState={trackingState}
        />
      )}

      {/* Once a status is found, the promo banner sits BELOW the results. */}
      {trackingData && !isNotFound && !isLoading && <TrackingPromoNew />}

      {isNotFound && trackingErrorNumber && !isLoading && (
        <div className={classes.notFoundSection}>
          <span className={classes.notFoundText}>
            <SearchIcon style={{ width: 28, height: 28, marginRight: 8 }} />
            {t('order-is-not-found')} {trackingErrorNumber}
          </span>
          <span className={classes.pleaseTryAgain}>
            {t('please-try-again')}
          </span>
        </div>
      )}

      <HelpBanner />

      <Backdrop open={isLoading} style={{ zIndex: 99999 }}>
        <CircularProgress />
      </Backdrop>
    </NewShell>
  )
}

export default TrackingNew
